import { Dispatch, SetStateAction, useState } from 'react';
import { styled } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { QModal, QModalTitle, QModalContent, QButton, QTextField, QTypography } from '@kw/quokka-ui';
import { sendHttpRequest } from '../../utils/network.service';
import { KwFormErrorMessage } from '../../kw-ui-components/KwFormErrorMessage';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInviteSent?: () => void;
  setUsersCount: Dispatch<SetStateAction<number>>;
  setInvitedUser?: Dispatch<SetStateAction<{ [key: string]: string | number | boolean } | null>>;
}

export const InviteUserModal = ({ isOpen, onClose, onInviteSent, setUsersCount, setInvitedUser }: InviteUserModalProps) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const [formErrors, setFormErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
  });

  const handleClose = () => {
    setFormErrors({
      firstName: false,
      lastName: false,
      email: false,
    });
    onClose();
  };

  const handleSendInvite = async () => {
    const errors = {
      firstName: !firstName.trim(),
      lastName: !lastName.trim(),
      email: !email.trim(),
    };

    setFormErrors(errors);

    try {
      const queryParams = new URLSearchParams({ email, firstName, lastName });
      const path = `${process.env.ORGANIZATION_SERVICE_URL}/organizations/user?${queryParams.toString()}`;

      await sendHttpRequest({
        path,
        method: 'POST',
        successMessage: 'Successfully invited user',
        errorMessage: 'Error inviting user',
        enqueueSnackbar,
      });
      // set temporary user data until the user is created
      setInvitedUser({
        name: `${firstName} ${lastName}`,
        email,
        last_login: '',
        logins_count: 0,
        created_at: new Date().toISOString(),
        picture: '',
        updated_at: new Date().toDateString(),
        user_id: '',
        temp: true,
      });
      // Reset form
      setFirstName('');
      setLastName('');
      setEmail('');

      if (onInviteSent) {
        onInviteSent();
        setUsersCount(prevCount => prevCount + 1);
      }
      onClose();
    } catch (err) {
      console.error('Error sending invite:', err);
    }
  };

  return (
    <QModal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="invite-user-dialog-title"
      scroll="body"
      title={
        <QModalTitle onClose={handleClose} closeIcon={<CloseIcon />}>
          <QTypography variant="h3Medium" id="invite-user-modal-title">
            Invite users
          </QTypography>
        </QModalTitle>
      }
      content={
        <QModalContent>
          <StyledRow>
            <StyledFormControl>
              <QTypography variant="h5Medium">First Name</QTypography>
              <QTextField
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder="First Name"
                error={Boolean(formErrors.firstName)}
              />
              <KwFormErrorMessage error={Boolean(formErrors.firstName)} reason="First name is required." />
            </StyledFormControl>

            <StyledFormControl>
              <QTypography variant="h5Medium">Last Name</QTypography>
              <QTextField
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                placeholder="Last Name"
                error={Boolean(formErrors.lastName)}
              />
              <KwFormErrorMessage error={Boolean(formErrors.firstName)} reason="Last name is required." />
            </StyledFormControl>
          </StyledRow>
          <QTypography variant="h5Medium">Email</QTypography>
          <StyledRow>
            <EmailFieldContainer>
              <QTextField value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" error={Boolean(formErrors.email)} />
            </EmailFieldContainer>
            <QButton variant="filled" onClick={handleSendInvite}>
              Send invite
            </QButton>
          </StyledRow>
          <KwFormErrorMessage error={Boolean(formErrors.email)} reason="Email is required." />
        </QModalContent>
      }
    />
  );
};

const EmailFieldContainer = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-self: flex-end;
`;

const StyledRow = styled('div')`
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
  align-items: center;
`;

const StyledFormControl = styled('div')`
  margin-top: 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  flex: 1; /* each child (firstName/lastName) takes half the row */
`;
